/* eslint-disable unicorn/no-thenable */

/**
 * A pair of functions that get and set a value to/from another value.
 *
 * Warning: These lenses do not always follow the lens laws! The name "lens"
 * comes from a similar concept in functional-programming, however, this lens
 * does not always follow the rules of true functional-programming lenses.
 *
 * -----
 *
 * A lens `l: Lens<S, T, A, B>` is pair of get and set functions that can be
 * used to convert functions from `A` to `B` into functions from `S` to `T`. The
 * dotted arrow below is the `f` argument of `l.modify(f, s)`.
 *
 * ```txt
 *  S >------set--> T
 *  |         |
 * get        |
 *  |         |
 *  v         ^
 *  A ······> B
 * ```
 *
 * -----
 *
 *  Lenses `x: Lens<S, T, A, B>` and `y: Lens<A, B, U, V>` can be composed
 * `x.then(y)`.
 *
 * ```txt
 *  S >------------x.set--> T
 *  |                |
 * x.get             |
 *  |                |
 *  v                ^
 *  A >-----y.set--> B
 *  |         |
 * y.get      |
 *  |         |
 *  v         ^
 *  U ······> V
 * ```
 */
export class Lens {
  constructor(get, set) {
    this.get = get;
    this.set = set;
  }

  /**
   * Compose this lens with another lens.
   */

  /**
   * Compose this lens with a getter function.
   */

  /**
   * Compose this lens with a setter function.
   */

  /**
   * Compose this lens with nothing. (This is essentially a no-op.)
   */

  /**
   * Compose this lens with a getter, setter, both, or neither.
   *
   * This overload is to prevent type errors in when this is used polymorphically.
   */

  then({
    get,
    set
  } = {}) {
    return new Lens(get ? s => get(this.get(s)) : this.get, set ? (a, s) => this.set(set(a, this.get(s)), s) : this.set);
  }
  modify(f, s) {
    return this.set(f(this.get(s)), s);
  }
}